/**
 * useSpotlight — composable for the Spotlight section.
 *
 * 文章列表与详情走 Nitro：`GET /api/spotlight`、`GET /api/spotlight/:slug`（数据来自 Sanity）。
 * 列表页 hero / SEO / FAQ 走 `GET /api/spotlight/list-config`（`spotlightList` 单例）。
 */

import { withSsrDependencyTiming } from '@/lib/ssr-timing';
import type { SiteLocale, TranslationAlternate } from '@/lib/site-locale';

export interface SpotlightListPost {
  _id: string;
  slug: string;
  language?: SiteLocale;
  translationKey?: string;
  alternates?: TranslationAlternate[];
  title: string;
  excerpt?: string | unknown[];
  /** True when excerpt is auto-filled from the first body paragraph (no `description` in frontmatter). */
  excerptDerivedFromBody?: boolean;
  image?: string;
  imageAlt?: string;
  publishedAt?: string;
  _updatedAt?: string;
  categories?: { title: string }[];
  category?: string;
  tags?: string[];
  readTime?: number;
  author?: SpotlightAuthor;
  seoTitle?: string;
  seoDescription?: string;
  keywords?: string[];
}

export interface SpotlightAuthor {
  name: string;
  avatar?: string;
  avatarUrl?: string;
}

export type SpotlightSerpData = Record<string, unknown>;

export interface SpotlightListConfig {
  language: SiteLocale;
  exists: boolean;
  seoTitle: string;
  seoDescription: string;
  keywords: string[];
  title: string;
  subtitle: string;
  faqTitle: string;
  faqItems: { question: string; answer: string }[];
  featuredSlugs: string[];
}

export interface SpotlightDetailPost extends SpotlightListPost {
  author?: SpotlightAuthor;
  /** Open Graph image alt (optional; falls back to `imageAlt`) */
  ogImageAlt?: string;
  body?: unknown[];
  bodyContent?: unknown[];
  bodyPortableText?: unknown[] | null;
  faq: { question: string; answer: string }[];
  youtubeLinks: { url: string; title?: string }[];
  canonicalUrl?: string;
  ogImage?: string;
  relatedPosts: {
    _id: string;
    slug: string;
    title: string;
    excerpt?: string | unknown[];
    image?: string;
    publishedAt?: string;
  }[];
}

export function useSpotlight() {
  async function fetchSpotlightPosts(locale: SiteLocale = 'en'): Promise<SpotlightListPost[]> {
    return await withSsrDependencyTiming(
      'api:spotlight:list',
      () => $fetch<SpotlightListPost[]>('/api/spotlight', { query: { locale } }),
      { url: `/api/spotlight?locale=${locale}`, kind: 'internal' }
    );
  }

  async function fetchSpotlightPostBySlug(
    slug: string,
    locale: SiteLocale = 'en'
  ): Promise<SpotlightDetailPost | null> {
    try {
      return await withSsrDependencyTiming(
        'api:spotlight:detail',
        () =>
          $fetch<SpotlightDetailPost>(`/api/spotlight/${encodeURIComponent(slug)}`, {
            query: { locale },
          }),
        {
          url: `/api/spotlight/${encodeURIComponent(slug)}?locale=${locale}`,
          kind: 'internal',
        }
      );
    } catch (err: unknown) {
      const e = err as { statusCode?: number };
      if (e?.statusCode === 404) return null;
      throw err;
    }
  }

  async function fetchRecommendedSpotlight(
    currentSlug: string,
    limit: number,
    locale: SiteLocale = 'en'
  ): Promise<{ id: string; slug: string; title: string; category: string; image: string }[]> {
    const all = await fetchSpotlightPosts(locale);
    return all
      .filter((p) => p.slug !== currentSlug)
      .slice(0, limit)
      .map((p) => ({
        id: p._id,
        slug: p.slug,
        title: p.title,
        category: p.categories?.[0]?.title || p.category || 'Spotlight',
        image:
          p.image ||
          'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop',
      }));
  }

  async function fetchSpotlightListConfig(locale: SiteLocale = 'en'): Promise<SpotlightListConfig> {
    return await withSsrDependencyTiming(
      'api:spotlight:list-config',
      () => $fetch<SpotlightListConfig>('/api/spotlight/list-config', { query: { locale } }),
      { url: `/api/spotlight/list-config?locale=${locale}`, kind: 'internal' }
    );
  }

  async function fetchSpotlightSerpBySlug(
    slug: string,
    locale: SiteLocale = 'en'
  ): Promise<SpotlightSerpData | null> {
    try {
      return await withSsrDependencyTiming(
        'api:spotlight:serp',
        () =>
          $fetch<SpotlightSerpData>(`/api/spotlight/${encodeURIComponent(slug)}/serp`, {
            query: { locale },
          }),
        {
          url: `/api/spotlight/${encodeURIComponent(slug)}/serp?locale=${locale}`,
          kind: 'internal',
        }
      );
    } catch (err: unknown) {
      const e = err as { statusCode?: number };
      if (e?.statusCode === 404) return null;
      throw err;
    }
  }

  return {
    fetchSpotlightPosts,
    fetchSpotlightPostBySlug,
    fetchSpotlightSerpBySlug,
    fetchRecommendedSpotlight,
    fetchSpotlightListConfig,
  };
}
